import TimeFormat from '../../helpers/TimeFormat';

export const OPEN_24_HRS = '24hrs';
export const DEFAULT_TIMESPAN = '00:00-00:00';

export type Hours = {[key: string]: string[]};

export const DAYS: {key: string; name: string}[] = [
  {key: 'mon_open_close', name: 'Monday'},
  {key: 'tue_open_close', name: 'Tuesday'},
  {key: 'wed_open_close', name: 'Wednesday'},
  {key: 'thu_open_close', name: 'Thursday'},
  {key: 'fri_open_close', name: 'Friday'},
  {key: 'sat_open_close', name: 'Saturday'},
  {key: 'sun_open_close', name: 'Sunday'}
];

export const ALL_DAYS = DAYS.map((day) => day.key);
export const WEEKDAYS = ALL_DAYS.slice(0, 5);
export const WEEKEND = ALL_DAYS.slice(5);

export function setDays(hours: Hours, keys: string[], spans: string[]): Hours {
  return keys.reduce((next, key) => ({...next, [key]: [...spans]}), {...hours});
}

export const EMPTY_HOURS: Hours = setDays({}, ALL_DAYS, []);

export function spansFor(hours: Hours, key: string): string[] {
  return (hours && hours[key]) || [];
}

export function isOpen(hours: Hours, key: string): boolean {
  return spansFor(hours, key).length > 0;
}

export function isOpen24(hours: Hours, key: string): boolean {
  return spansFor(hours, key)[0] === OPEN_24_HRS;
}

export function openDayCount(hours: Hours): number {
  return DAYS.filter((day) => isOpen(hours, day.key)).length;
}

export function setSpans(hours: Hours, key: string, spans: string[]): Hours {
  return {...hours, [key]: spans};
}

export function toggleDay(hours: Hours, key: string): Hours {
  return setSpans(hours, key, isOpen(hours, key) ? [] : [DEFAULT_TIMESPAN]);
}

export function toggle24(hours: Hours, key: string): Hours {
  return setSpans(
    hours,
    key,
    isOpen24(hours, key) ? [DEFAULT_TIMESPAN] : [OPEN_24_HRS]
  );
}

export function addSpan(hours: Hours, key: string): Hours {
  return setSpans(hours, key, [...spansFor(hours, key), DEFAULT_TIMESPAN]);
}

export function removeSpan(hours: Hours, key: string, index: number): Hours {
  return setSpans(
    hours,
    key,
    spansFor(hours, key).filter((_span, i) => i !== index)
  );
}

export function updateSpan(
  hours: Hours,
  key: string,
  index: number,
  span: string
): Hours {
  return setSpans(
    hours,
    key,
    spansFor(hours, key).map((existing, i) => (i === index ? span : existing))
  );
}

export function splitSpan(span: string): {open: string; close: string} {
  const [open, close] = String(span || DEFAULT_TIMESPAN).split('-');
  return {open: open || '00:00', close: close || '00:00'};
}

export function joinSpan(open: string, close: string): string {
  return `${open}-${close}`;
}

export function hasAnyHours(hours: Hours): boolean {
  return openDayCount(hours) > 0;
}

const CLOCK_TIME = /^\d\d:\d\d$/;

function formatHour(hour: string): string {
  if (CLOCK_TIME.test(hour)) return TimeFormat.format_time(hour);

  return hour.charAt(0).toUpperCase() + hour.slice(1);
}

export function formatSpan(span: string): string {
  if (span === OPEN_24_HRS) return 'Open 24 hours';

  const {open, close} = splitSpan(span);

  return `${formatHour(open)} - ${formatHour(close)}`;
}
